import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  SipAccount,
  SipAccountDto,
  toUpdateSipAccountsRequest,
} from '../../models/RoboCall/SipAccount';
import { Status } from '../../models/common/Status';
import { RootState } from '../store';
import { apiClientWithApi } from '../../utils/http';

export interface SipAccountsState {
  sipAccounts: SipAccount[];
  status: Status;
  saving: boolean;
}

const initialState: SipAccountsState = {
  sipAccounts: [],
  saving: false,
  status: {
    responseLoading: false,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

export const getSipAccounts = createAsyncThunk<
  SipAccount[],
  void,
  { rejectValue: string }
>('sipaccount/getSipAccounts', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClientWithApi.get<SipAccount[]>('sipaccount');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(error.message);
    }
  }
  return rejectWithValue('Серверная ошибка!');
});

export const updateSipAccounts = createAsyncThunk<
  SipAccount[],
  SipAccountDto[],
  { rejectValue: string }
>(
  'sipaccount/updateSipAccounts',
  async (accounts: SipAccount[], { rejectWithValue }) => {
    try {
      const response = await apiClientWithApi.post<SipAccount[]>(
        'sipaccount',
        toUpdateSipAccountsRequest(accounts),
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data ?? error.message);
      }

      return rejectWithValue('Не удалось сохранить аккаунты!');
    }
  },
);

export const sipAccountSlice = createSlice({
  name: 'sipAccount',
  initialState,
  reducers: {
    updateRequestStatus: (state) => {
      state.status.requestSuccess = null;
      state.status.requestError = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSipAccounts.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
      })
      .addCase(getSipAccounts.fulfilled, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        state.sipAccounts = action.payload;
      })
      .addCase(getSipAccounts.rejected, (state, action) => {
        state.status.responseLoading = false;
        state.status.responseError =
          action.payload ?? action.error.message ?? 'Серверная ошибка!';
      })
      .addCase(updateSipAccounts.pending, (state) => {
        state.saving = true;
        state.status.requestSuccess = null;
        state.status.requestError = null;
      })
      .addCase(updateSipAccounts.fulfilled, (state, action) => {
        state.saving = false;
        state.status.requestSuccess = 'Изменения сохранены';
        state.status.requestError = null;
        state.sipAccounts = action.payload;
      })
      .addCase(updateSipAccounts.rejected, (state) => {
        state.saving = false;
        state.status.requestSuccess = null;
        state.status.requestError = 'Не удалось сохранить аккаунты';
      });
  },
});

export default sipAccountSlice.reducer;

export const { updateRequestStatus } = sipAccountSlice.actions;

export const selectSipAccounts = (state: RootState) =>
  state.sipAccounts.sipAccounts;
export const sipAccountsStatus = (state: RootState) => state.sipAccounts.status;
export const selectSipAccountsSaving = (state: RootState) =>
  state.sipAccounts.saving;
